import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS, API_BASE_URL } from '../../constants/api-urls';
import './ReviewInspectorQuotations.scss';

const API_BASE_URL_MEDIA = API_BASE_URL.replace('/api/v1', '');

// Match InspectionRFQDetails / AuctionList – full path handling so product image loads
const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return '';
  const path = typeof accessPath === 'string' ? accessPath : String(accessPath);
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const baseUrl = API_BASE_URL_MEDIA;
  if (path.includes('C:/usr/local/b2b/seller/AuctionProduct') || path.includes('C:\\usr\\local\\b2b\\seller\\AuctionProduct')) {
    const match = path.match(/AuctionProduct[\/\\](\d+)[\/\\]([^\/\\]+)$/);
    if (match) return `${baseUrl}/auction-media/${match[1]}/${match[2]}`;
    const tempMatch = path.match(/AuctionProduct[\/\\]temp[\/\\]([^\/\\]+)$/);
    if (tempMatch) return `${baseUrl}/auction-media/temp/${tempMatch[1]}`;
  }
  if (path.startsWith('/auction-media/')) return `${baseUrl}${path}`;
  let cleanPath = path;
  if (cleanPath.startsWith(':3000')) cleanPath = cleanPath.replace(/^:3000/, '');
  if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
  if (!cleanPath.startsWith('/auction-media')) {
    const fileName = cleanPath.split('/').pop();
    if (fileName && auctionId) return `${baseUrl}/auction-media/${auctionId}/${fileName}`;
    return fileName ? `${baseUrl}/media/${fileName}` : '';
  }
  return `${baseUrl}${cleanPath}`;
};

export default function ReviewInspectorQuotations() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [auction, setAuction] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [auctionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const rfqResponse = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_RFQ_BY_AUCTION(auctionId));
      if (rfqResponse && rfqResponse.success && rfqResponse.data) {
        const rfqData = rfqResponse.data;
        setRfq(rfqData);
        const rfqId = rfqData.rfq_id;
        const quotationsResponse = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_QUOTATIONS(rfqId));
        if (quotationsResponse && quotationsResponse.success) {
          const list = quotationsResponse.data || [];
          setQuotations(list);
          const selected = list.find(q => q.status === 'selected');
          if (selected) setSelectedQuotation(selected);
        }
      }
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) setAuction(auctionResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuotation = (quotation) => setSelectedQuotation(quotation);
  const handleDeselectQuotation = () => setSelectedQuotation(null);

  const handleAcceptQuotation = async () => {
    if (!selectedQuotation) {
      toast.error('Please select an inspector first');
      return;
    }
    try {
      setAccepting(true);
      const response = await CommonService.post(API_URLS.AUCTION.ACCEPT_INSPECTION_QUOTATION(selectedQuotation.quotation_id));
      if (response && response.success) {
        toast.success('Inspector selected successfully!');
        navigate(`/auction/${auctionId}/inspection-initiated`, { state: { quotation: selectedQuotation } });
      } else {
        throw new Error(response?.message || 'Failed to accept quotation');
      }
    } catch (error) {
      console.error('Error accepting quotation:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to accept quotation');
    } finally {
      setAccepting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) return <div className="review-inspector-quotations loading">Loading...</div>;
  if (!rfq || !auction) return <div className="review-inspector-quotations error">RFQ or auction not found</div>;

  const bestPriceQuotation = quotations.length > 0
    ? quotations.reduce((min, q) => q.quote_amount < min.quote_amount ? q : min, quotations[0])
    : null;

  // Count: prefer list length when loaded, else use RFQ response (quotes_received_count from API)
  const countFromRfq = Number(rfq?.quotes_received_count);
  const displayQuotesCount = Math.max(
    Number.isNaN(countFromRfq) ? 0 : countFromRfq,
    Array.isArray(quotations) ? quotations.length : 0
  );

  const productImageUrl = (() => {
    let img = null;
    if (!auction?.images) return '';
    if (Array.isArray(auction.images) && auction.images.length > 0) img = auction.images[0];
    else if (typeof auction.images === 'string') {
      try {
        const parsed = JSON.parse(auction.images);
        img = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : (parsed && typeof parsed === 'string' ? parsed : null);
      } catch {
        img = auction.images;
      }
    }
    const aid = auction.auction_id ?? auction.id ?? auctionId;
    return img ? getMediaUrl(img, aid) : '';
  })();

  return (
    <div className="review-inspector-quotations">
      <div className="container-fluid">
        {/* Header: green check icon + title + subtitle */}
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <div className="header-content">
            <div className="header-icon green-check">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="currentColor"/>
                <path d="M8 12l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h1>Review & Accept Inspector Quotations</h1>
              <p>Compare quotes and select the best inspector for your needs</p>
            </div>
          </div>
        </div>

        {/* Top row: Product Being Inspected (light blue) + RFQ Details (light pink) */}
        <div className="top-info-row">
          <div className="info-card product-card">
            <div className="card-icon orange">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M2 6h20v12H2V6zM2 6l2-4h16l2 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Product Being Inspected</h3>
            <div className="product-row">
              <div className="product-image-wrap">
                {productImageUrl ? (
                  <>
                    <img
                      src={productImageUrl}
                      alt={auction.product_name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) placeholder.classList.remove('hidden');
                      }}
                    />
                    <div className="placeholder-image hidden">No Image</div>
                  </>
                ) : (
                  <div className="placeholder-image">No Image</div>
                )}
              </div>
              <div className="product-content">
                <p className="product-desc">{auction.product_description || 'No description available'}</p>
                <p className="product-bid">Proposed Bid: {formatCurrency(auction.winning_bid_amount ?? rfq?.winning_bid_amount ?? 0)}</p>
                <p className="product-location">Location: {[auction.city, auction.state].filter(Boolean).join(', ') || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="info-card rfq-card">
            <div className="card-icon purple">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 12h6M9 16h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>RFQ Details</h3>
            <div className="rfq-details">
              <div className="rfq-item">
                <span className="label">RFQ ID:</span>
                <span className="value">{rfq.rfq_number || `RFQ-IN-${rfq.rfq_id}`}</span>
              </div>
              <div className="rfq-item">
                <span className="label">Quotes Received:</span>
                <span className="value">{displayQuotesCount}</span>
              </div>
            </div>
            {selectedQuotation && (
              <div className="rfq-status">1 quote selected</div>
            )}
          </div>
        </div>

        {/* Inspector Quotations section */}
        <h2 className="section-title">Inspector Quotations ({displayQuotesCount})</h2>
        <div className="quotations-list">
          {quotations.map((quotation) => {
            const isSelected = selectedQuotation?.quotation_id === quotation.quotation_id;
            const isBestPrice = bestPriceQuotation?.quotation_id === quotation.quotation_id;
            return (
              <div key={quotation.quotation_id} className={`quotation-card ${isSelected ? 'selected' : ''}`}>
                <div className="quotation-header">
                  <div className="inspector-info">
                    <div className="inspector-avatar">
                      {isSelected ? (
                        <div className="icon-green-check">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M8 12l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      ) : (
                        <div className="icon-person">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 10c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="currentColor"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="inspector-meta">
                      <div className="inspector-name-row">
                        <span className="inspector-name">{quotation.inspector_name || 'Inspector Name'}</span>
                        {isSelected && <span className="badge selected-badge">SELECTED</span>}
                        {isBestPrice && !isSelected && <span className="badge best-price-badge">Best Price</span>}
                      </div>
                      <div className="inspector-company">{quotation.inspector_company || 'Company Name'}</div>
                      <div className="inspector-badges">
                        <span className="badge-item">Certified Inspector</span>
                        <span className="badge-item">Quality Evaluator</span>
                      </div>
                      <div className="inspector-rating">
                        <span className="star">★</span>
                        <span className="rating-text">4.8 (Inspections)</span>
                      </div>
                      <div className="contact-icons">
                        <a href={`tel:${quotation.inspector_phone || ''}`} className="contact-btn" title="Phone">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                        </a>
                        <a href={`mailto:${quotation.inspector_email || ''}`} className="contact-btn" title="Email">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="quotation-fee-block">
                    <div className="fee-label">Inspection Fee</div>
                    <div className="fee-amount">{formatCurrency(quotation.quote_amount)}</div>
                    <div className="submitted-date">Submitted: {formatDateShort(quotation.submitted_at)}</div>
                  </div>
                </div>

                <div className="quotation-details-grid">
                  <div className="detail-col schedule-col">
                    <div className="detail-icon purple">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                    </div>
                    <h4>Proposed Schedule</h4>
                    <div className="detail-item"><span className="value">{formatDate(quotation.proposed_date)}</span></div>
                    <div className="detail-item"><span className="value">{quotation.proposed_time || 'N/A'}</span></div>
                    <div className="detail-item"><span className="value">{quotation.estimated_duration_hours || 'N/A'}</span></div>
                  </div>
                  <div className="detail-col approach-col">
                    <div className="detail-icon blue">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
                    </div>
                    <h4>Inspection Approach</h4>
                    <p className="approach-text">{quotation.inspection_approach || 'N/A'}</p>
                  </div>
                </div>

                {quotation.additional_notes && (
                  <div className="additional-notes">
                    <div className="notes-icon yellow">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l10 20H2L12 2z"/><path d="M12 9v4M12 17h.01"/></svg>
                    </div>
                    <p>{quotation.additional_notes}</p>
                  </div>
                )}

                <div className="quotation-footer">
                  <div className="footer-actions">
                    {isSelected ? (
                      <button type="button" className="btn-deselect" onClick={handleDeselectQuotation}>Deselect</button>
                    ) : (
                      <button type="button" className="btn-select" onClick={() => handleSelectQuotation(quotation)}>Select This Inspector</button>
                    )}
                  </div>
                  <div className="footer-contact">
                    <a href={`tel:${quotation.inspector_phone || ''}`} className="contact-btn"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg></a>
                    <a href={`mailto:${quotation.inspector_email || ''}`} className="contact-btn"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg></a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Inspector Selected bar + Begin Inspection */}
        {selectedQuotation && (
          <div className="inspector-selected-bar">
            <div className="selected-info">
              <div className="icon-green-check">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8 12l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <span className="label">Inspector Selected</span>
                <span className="value">{selectedQuotation.inspector_name} - {formatCurrency(selectedQuotation.quote_amount)}</span>
              </div>
            </div>
            <button type="button" className="btn-begin-inspection" onClick={handleAcceptQuotation} disabled={accepting}>
              {accepting ? 'Processing...' : 'Begin Inspection'}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
            </button>
          </div>
        )}

        {/* Important Notes */}
        <div className="important-notes-card">
          <div className="notes-icon blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
          </div>
          <div className="notes-content">
            <h4>Important Notes</h4>
            <ul>
              <li>Review all quotations carefully before selecting an inspector.</li>
              <li>Compare pricing, deliverables, and inspection approach.</li>
              <li>Contact inspectors directly if you need clarification.</li>
              <li>Once selected, the inspector will be notified to begin inspection.</li>
              <li>Inspection must be completed within 72 hours of scheduling.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
